// Color utility functions

/**
 * Generates a cohesive set of natural colors based on two main tones
 * @returns {object} Object containing color values for different building elements
 */
export const generateCoherentColorSet = () => {
  // Define earthy color palettes with HSL ranges
  const naturalPalettes = [
    // Greys
    { h: [0, 30], s: [0, 10], l: [65, 90] },
    // Darker Greys
    { h: [0, 30], s: [0, 10], l: [35, 55] },
    // Light greens (sage, olive)
    { h: [60, 140], s: [15, 35], l: [60, 85] },
    // Light browns
    { h: [20, 40], s: [30, 50], l: [60, 80] },
    // Darker browns
    { h: [15, 35], s: [40, 70], l: [25, 45] },
    // Beige/Sand colors
    { h: [30, 50], s: [20, 45], l: [75, 90] },
    // Rust/Terracotta
    { h: [10, 30], s: [40, 60], l: [50, 65] },
    // Yellows (wheat, mustard)
    { h: [40, 60], s: [40, 70], l: [60, 85] },
    // Light Blues
    { h: [180, 220], s: [15, 40], l: [70, 90] },
    // Whites (off-white, cream)
    { h: [30, 60], s: [5, 20], l: [88, 97] }
  ];

  // Select two distinct palettes for primary and secondary tones
  const primaryPaletteIndex = Math.floor(Math.random() * naturalPalettes.length);
  let secondaryPaletteIndex;
  do {
    secondaryPaletteIndex = Math.floor(Math.random() * naturalPalettes.length);
  } while (secondaryPaletteIndex === primaryPaletteIndex);

  const primaryPalette = naturalPalettes[primaryPaletteIndex];
  const secondaryPalette = naturalPalettes[secondaryPaletteIndex];

  // Generate base colors for primary and secondary tones
  const primaryColor = generateColorFromPalette(primaryPalette);
  const secondaryColor = generateColorFromPalette(secondaryPalette);

  // Extract HSL components for variations
  const primaryHSL = hexToHSL(primaryColor);
  const secondaryHSL = hexToHSL(secondaryColor);

  // Create variations of the primary and secondary colors
  return {
    // Main building elements (primary tone variations)
    facadeColor: primaryColor,
    groundFloorColor: createVariation(primaryHSL, { l: -15 }), // Darker variation for ground floor
    sideWallColor: createVariation(primaryHSL, { s: -10, l: -5 }),
    
    // Secondary elements (secondary tone variations)
    balconyColor: secondaryColor,
    awningColor: createVariation(secondaryHSL, { s: +10, l: -10 }),
    
    // Roof elements - always grey/white variations
    roofColor: generateGreyColor(30, 50), // Medium to dark grey
    roofAccessColor: generateGreyColor(15, 30), // Darker grey for roof access
    
    // Vitrine - baby blue color with slight variation based on secondary tone
    vitrineColor: createVariation({ h: 210, s: 60, l: 75 }, { h: secondaryHSL.h % 20 - 10 }),
    vitrineFrameColor: '#ffffff', // Default white frame color
    
    // Vitrine awning - completely random color
    vitrineAwningColor: generateRandomColor(),
    
    // Window frame (usually light/white)
    windowFrameColor: '#ffffff',
    
    // Keep WindowColorA and WindowColorB as their original values in BuildingContext
  };
};

/**
 * Generates a completely random color
 * @returns {string} Hex color code
 */
export const generateRandomColor = () => {
  const h = Math.floor(Math.random() * 360);
  const s = Math.floor(Math.random() * 100);
  const l = Math.floor(Math.random() * 80) + 20; // Ensure some brightness (20-100)
  
  return hslToHex(h, s, l);
};

/**
 * Generates a grey color with the specified lightness range
 * @param {number} minL - Minimum lightness value (0-100)
 * @param {number} maxL - Maximum lightness value (0-100)
 * @returns {string} Hex color code of a grey
 */
const generateGreyColor = (minL, maxL) => {
  // Random lightness within the range
  const l = Math.floor(minL + Math.random() * (maxL - minL));
  
  // Slight random warmth/coolness (0-30 hue)
  const h = Math.floor(Math.random() * 30);
  
  // Very low saturation for grey appearance
  const s = Math.floor(Math.random() * 10);
  
  return hslToHex(h, s, l);
};

/**
 * Generates a random color from the specified palette
 * @param {object} palette - HSL range definition
 * @returns {string} Hex color
 */
const generateColorFromPalette = (palette) => {
  const h = Math.floor(palette.h[0] + Math.random() * (palette.h[1] - palette.h[0]));
  const s = Math.floor(palette.s[0] + Math.random() * (palette.s[1] - palette.s[0]));
  const l = Math.floor(palette.l[0] + Math.random() * (palette.l[1] - palette.l[0]));
  
  return hslToHex(h, s, l);
};

/**
 * Creates a variation of a color by adjusting its HSL values
 * @param {object} hsl - HSL color object
 * @param {object} adjustments - Adjustments to make to h, s, l values
 * @returns {string} Hex color code
 */
const createVariation = (hsl, adjustments = {}) => {
  const h = (hsl.h + (adjustments.h || 0)) % 360;
  const s = Math.max(0, Math.min(100, hsl.s + (adjustments.s || 0)));
  const l = Math.max(0, Math.min(100, hsl.l + (adjustments.l || 0)));
  
  return hslToHex(h, s, l);
};

/**
 * Converts a hex color to HSL
 * @param {string} hex - Hex color code
 * @returns {object} HSL values object
 */
const hexToHSL = (hex) => {
  // Convert hex to RGB first
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16) / 255;
    g = parseInt(hex[2] + hex[2], 16) / 255;
    b = parseInt(hex[3] + hex[3], 16) / 255;
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16) / 255;
    g = parseInt(hex.substring(3, 5), 16) / 255;
    b = parseInt(hex.substring(5, 7), 16) / 255;
  }

  // Then to HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};

/**
 * Converts HSL color values to hex string
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color code
 */
export const hslToHex = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Darkens a hex color by a specified amount
 * @param {string} color - Hex color code (with or without #)
 * @param {number} amount - Amount to darken by (0-1)
 * @returns {string} Darkened hex color code
 */
export const darkenHexColor = (color, amount) => {
  // Remove the # if it exists
  let hex = color.replace('#', '');
  
  // Parse the RGB components
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Darken each component
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Lightens a hex color by a specified amount
 * @param {string} color - Hex color code (with or without #)
 * @param {number} amount - Amount to lighten by (0-1)
 * @returns {string} Lightened hex color code
 */
export const lightenHexColor = (color, amount) => {
  // Remove the # if it exists
  let hex = color.replace('#', '');
  
  // Parse the RGB components
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Lighten each component
  r = Math.min(255, Math.round(r + (255 - r) * amount));
  g = Math.min(255, Math.round(g + (255 - g) * amount));
  b = Math.min(255, Math.round(b + (255 - b) * amount));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Default color constants that can be used as fallbacks or initial values
 */
export const COLOR_DEFAULTS = {
  // Main building colors
  facadeColor: '#bebebe',
  groundFloorColor: '#4d4d4d',
  sideWallColor: '#9c9c9c',
  
  // Window colors
  WindowColorA: '#000080', // Window frame/border
  WindowColorB: '#b8ebff', // Window glass
  windowFrameColor: '#ffffff',
  
  // Feature colors
  balconyColor: '#dedede',
  awningColor: '#9e9e9e',
  
  // Roof colors
  roofColor: '#808080',
  roofAccessColor: '#404040',
};

export default COLOR_DEFAULTS; 